<template>
  <div class="view-container">
    <!-- Header -->
    <div class="view-header" style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;">
      <div>
        <h1 class="view-title">多租戶管理</h1>
        <p class="view-subtitle">各網域用量統計與管理（超級管理員專屬）</p>
      </div>
      <div style="display:flex;gap:10px;align-items:center;">
        <button class="btn btn-secondary btn-sm" @click="load" :disabled="loading">
          <svg v-if="loading" class="spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
          重新整理
        </button>
        <button class="btn btn-primary btn-sm" @click="openAddDomain">
          ＋ 新增網域
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:14px;margin-bottom:24px;">
      <div class="card p-4" style="text-align:center;">
        <div style="font-size:1.8rem;font-weight:700;color:var(--brand-600);">{{ domains.length }}</div>
        <div class="text-sm" style="color:var(--gray-500);margin-top:4px;">網域總數</div>
      </div>
      <div class="card p-4" style="text-align:center;">
        <div style="font-size:1.8rem;font-weight:700;color:var(--gray-700);">{{ totalUsers }}</div>
        <div class="text-sm" style="color:var(--gray-500);margin-top:4px;">使用者總數</div>
      </div>
      <div class="card p-4" style="text-align:center;">
        <div style="font-size:1.8rem;font-weight:700;color:var(--color-success);">{{ totalContacts }}</div>
        <div class="text-sm" style="color:var(--gray-500);margin-top:4px;">聯絡人總數</div>
      </div>
      <div class="card p-4" style="text-align:center;">
        <div style="font-size:1.8rem;font-weight:700;color:var(--color-warning);">{{ totalCampaigns }}</div>
        <div class="text-sm" style="color:var(--gray-500);margin-top:4px;">行銷活動總數</div>
      </div>
    </div>

    <!-- Search -->
    <div style="margin-bottom:16px;">
      <input v-model="searchQ" type="text" class="input" style="max-width:320px;" placeholder="搜尋網域名稱…" />
    </div>

    <!-- Loading -->
    <div v-if="loading && !domains.length" class="card p-8 text-center" style="color:var(--gray-400);">
      <div class="spin" style="width:28px;height:28px;border:3px solid var(--border);border-top-color:var(--brand-500);border-radius:50%;margin:0 auto 12px;"></div>
      載入中…
    </div>

    <!-- Domains Table -->
    <div v-else class="card" style="overflow:hidden;">
      <table class="data-table w-full">
        <thead>
          <tr>
            <th>網域</th>
            <th>使用者</th>
            <th>聯絡人</th>
            <th>行銷活動</th>
            <th>LINE 帳號</th>
            <th>最後活動</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!filteredDomains.length">
            <td colspan="7" class="text-center py-6" style="color:var(--gray-400);">無符合條件的網域</td>
          </tr>
          <tr
            v-for="d in filteredDomains"
            :key="d.id"
            style="cursor:pointer;"
            @click="openDetail(d)"
          >
            <td>
              <div style="font-weight:600;color:var(--gray-900);">{{ d.name }}</div>
              <div class="text-xs" style="color:var(--gray-400);">ID {{ d.id }}</div>
            </td>
            <td>
              <div class="flex items-center gap-1">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color:var(--gray-400);"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                <span>{{ d.stats.users }}</span>
                <span v-if="d.max_users > 0" class="text-xs" style="color:var(--gray-400);">/ {{ d.max_users }}</span>
              </div>
              <div v-if="d.max_users > 0" class="mt-1 w-16 rounded-full overflow-hidden" style="height:4px;background:var(--gray-200);">
                <div class="h-full rounded-full" :style="{ width: Math.min(100, d.stats.users / d.max_users * 100) + '%', background: d.stats.users >= d.max_users ? 'var(--color-danger)' : 'var(--brand-500)' }"></div>
              </div>
            </td>
            <td>{{ d.stats.contacts }}</td>
            <td>{{ d.stats.campaigns }}</td>
            <td>
              <span v-if="d.stats.line_accounts > 0" class="badge badge-success">{{ d.stats.line_accounts }} 個</span>
              <span v-else class="badge badge-default">—</span>
            </td>
            <td class="text-xs" style="color:var(--gray-500);">
              {{ d.stats.last_campaign_at ? fmtDate(d.stats.last_campaign_at) : '—' }}
            </td>
            <td>
              <div style="display:flex;gap:6px;" @click.stop>
                <button class="btn btn-xs btn-outline" @click="openDetail(d)">詳情</button>
                <button class="btn btn-xs" style="color:var(--color-danger);border-color:var(--color-danger);" @click="confirmDelete(d)">刪除</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add Domain Modal -->
    <div v-if="showAddModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div class="card shadow-xl w-full max-w-sm">
        <div class="card-header flex justify-between items-center">
          <span class="font-bold">新增網域</span>
          <button @click="showAddModal=false" class="text-xl" style="color:var(--gray-400);">×</button>
        </div>
        <div class="card-body space-y-3">
          <div>
            <label class="block text-xs font-bold mb-1" style="color:var(--gray-600);">網域名稱 *</label>
            <input v-model="addForm.name" class="input w-full" placeholder="example.com" autofocus />
          </div>
        </div>
        <div class="p-4 border-t flex justify-end gap-2" style="border-color:var(--border);">
          <button @click="showAddModal=false" class="btn btn-sm btn-outline">取消</button>
          <button @click="submitAdd" :disabled="!addForm.name || adding" class="btn btn-sm btn-primary">
            {{ adding ? '新增中...' : '確認新增' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Detail Drawer -->
    <div v-if="selectedDomain" class="fixed inset-0 bg-black/40 flex items-center justify-end z-50" @click.self="selectedDomain=null">
      <div class="card h-full overflow-y-auto" style="width:420px;max-width:95vw;border-radius:0;border-right:none;">
        <div class="card-header flex justify-between items-center sticky top-0" style="background:var(--surface);z-index:1;">
          <span class="font-bold">{{ selectedDomain.name }}</span>
          <button @click="selectedDomain=null" class="text-xl" style="color:var(--gray-400);">×</button>
        </div>
        <div class="card-body space-y-4">
          <!-- Stats -->
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
            <div class="card p-3 text-center">
              <div style="font-size:1.5rem;font-weight:700;color:var(--brand-600);">
                {{ selectedDomain.stats.users }}
                <span v-if="selectedDomain.max_users > 0" class="text-sm font-normal" style="color:var(--gray-400);">/{{ selectedDomain.max_users }}</span>
              </div>
              <div class="text-xs" style="color:var(--gray-500);">使用者</div>
            </div>
            <div class="card p-3 text-center">
              <div style="font-size:1.5rem;font-weight:700;color:var(--color-success);">{{ selectedDomain.stats.contacts }}</div>
              <div class="text-xs" style="color:var(--gray-500);">聯絡人</div>
            </div>
            <div class="card p-3 text-center">
              <div style="font-size:1.5rem;font-weight:700;color:var(--color-warning);">{{ selectedDomain.stats.campaigns }}</div>
              <div class="text-xs" style="color:var(--gray-500);">行銷活動</div>
            </div>
            <div class="card p-3 text-center">
              <div style="font-size:1.5rem;font-weight:700;color:var(--gray-700);">{{ selectedDomain.stats.line_accounts }}</div>
              <div class="text-xs" style="color:var(--gray-500);">LINE 帳號</div>
            </div>
          </div>

          <!-- Quota Settings -->
          <div class="card p-4">
            <div class="flex items-center justify-between mb-3">
              <div class="font-semibold" style="color:var(--gray-700);">資源配額設定</div>
              <span class="text-xs" style="color:var(--gray-400);">0 = 無限制</span>
            </div>
            <div class="space-y-3">
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;align-items:center;">
                <label class="text-sm" style="color:var(--gray-600);">最大使用者數</label>
                <input v-model.number="quotaForm.max_users" type="number" min="0" class="input text-sm" placeholder="0=無限" />
              </div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;align-items:center;">
                <label class="text-sm" style="color:var(--gray-600);">最大別名數</label>
                <input v-model.number="quotaForm.max_aliases" type="number" min="0" class="input text-sm" placeholder="0=無限" />
              </div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;align-items:center;">
                <label class="text-sm" style="color:var(--gray-600);">儲存上限 (MB)</label>
                <input v-model.number="quotaForm.max_quota_mb" type="number" min="0" class="input text-sm" placeholder="0=無限" />
              </div>
              <div class="flex justify-end gap-2 pt-1">
                <button @click="resetQuotaForm" class="btn btn-outline btn-sm">重設</button>
                <button @click="saveQuota" :disabled="quotaSaving" class="btn btn-primary btn-sm">
                  {{ quotaSaving ? '儲存中…' : '儲存配額' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Billing Summary -->
          <div class="card p-4">
            <div class="flex items-center justify-between mb-3">
              <div class="font-semibold" style="color:var(--gray-700);">帳單 / 用量統計</div>
              <button @click="loadBillingSummary(selectedDomain)" :disabled="billingLoading" class="btn btn-outline btn-xs">
                {{ billingLoading ? '載入中...' : '重新整理' }}
              </button>
            </div>
            <div v-if="billingLoading" class="text-sm text-center py-2" style="color:var(--gray-400);">載入中...</div>
            <div v-else-if="billingSummary" class="space-y-3">
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
                <div class="card p-3 text-center">
                  <div style="font-size:1.3rem;font-weight:700;color:var(--brand-600);">{{ billingSummary.month_emails_sent.toLocaleString() }}</div>
                  <div class="text-xs mt-1" style="color:var(--gray-500);">本月發送郵件</div>
                </div>
                <div class="card p-3 text-center">
                  <div style="font-size:1.3rem;font-weight:700;color:var(--color-warning);">{{ billingSummary.month_campaigns }}</div>
                  <div class="text-xs mt-1" style="color:var(--gray-500);">本月活動數</div>
                </div>
                <div class="card p-3 text-center">
                  <div style="font-size:1.3rem;font-weight:700;color:var(--gray-700);">{{ billingSummary.total_emails_sent.toLocaleString() }}</div>
                  <div class="text-xs mt-1" style="color:var(--gray-500);">累計發送</div>
                </div>
                <div class="card p-3 text-center">
                  <div style="font-size:1.3rem;font-weight:700;color:var(--color-success);">{{ billingSummary.total_campaigns }}</div>
                  <div class="text-xs mt-1" style="color:var(--gray-500);">累計活動</div>
                </div>
              </div>
              <!-- Quota bars -->
              <div v-if="billingSummary.max_users > 0" class="space-y-1">
                <div class="flex justify-between text-xs" style="color:var(--gray-600);">
                  <span>使用者配額</span>
                  <span>{{ selectedDomain?.stats?.users || 0 }} / {{ billingSummary.max_users }}</span>
                </div>
                <div class="w-full rounded-full overflow-hidden" style="height:6px;background:var(--gray-200);">
                  <div class="h-full rounded-full" :style="{ width: Math.min(100, (selectedDomain?.stats?.users || 0) / billingSummary.max_users * 100) + '%', background: (selectedDomain?.stats?.users || 0) >= billingSummary.max_users ? 'var(--color-danger)' : 'var(--brand-500)' }"></div>
                </div>
              </div>
              <div v-if="billingSummary.max_quota_bytes > 0" class="space-y-1">
                <div class="flex justify-between text-xs" style="color:var(--gray-600);">
                  <span>儲存配額</span>
                  <span>上限 {{ Math.round(billingSummary.max_quota_bytes / 1024 / 1024) }} MB</span>
                </div>
              </div>
            </div>
            <div v-else class="text-sm text-center py-2" style="color:var(--gray-400);">點擊「重新整理」載入帳單統計</div>
          </div>

          <!-- Domain Users -->
          <div>
            <div class="font-semibold mb-2" style="color:var(--gray-700);">網域使用者</div>
            <div v-if="detailUsersLoading" style="color:var(--gray-400);" class="text-sm">載入中…</div>
            <div v-else-if="!detailUsers.length" class="text-sm" style="color:var(--gray-400);">尚無使用者</div>
            <div v-else class="space-y-2">
              <div v-for="u in detailUsers" :key="u.id" style="display:flex;align-items:center;gap:8px;padding:8px;border-radius:8px;border:1px solid var(--border);">
                <div style="width:32px;height:32px;border-radius:50%;background:var(--brand-100);color:var(--brand-600);display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:13px;flex-shrink:0;">
                  {{ (u.email || '?')[0].toUpperCase() }}
                </div>
                <div style="flex:1;min-width:0;">
                  <div class="text-sm font-medium truncate" style="color:var(--gray-900);">{{ u.email }}</div>
                  <div class="text-xs" style="color:var(--gray-400);">{{ roleLabel(u.role) }}</div>
                </div>
                <span :class="u.is_active ? 'badge-success' : 'badge-default'" class="badge text-xs">{{ u.is_active ? '啟用' : '停用' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useAppStore, ADMIN_API } from '../store/app.js'

const store = useAppStore()

const domains = ref([])
const loading = ref(false)
const searchQ = ref('')

const showAddModal = ref(false)
const addForm = ref({ name: '' })
const adding = ref(false)

const selectedDomain = ref(null)
const detailUsers = ref([])
const detailUsersLoading = ref(false)

const quotaForm = ref({ max_users: 0, max_aliases: 0, max_quota_mb: 0 })
const quotaSaving = ref(false)
const billingSummary = ref(null)
const billingLoading = ref(false)

function resetQuotaForm() {
  const d = selectedDomain.value
  quotaForm.value = {
    max_users: d?.max_users ?? 0,
    max_aliases: d?.max_aliases ?? 0,
    max_quota_mb: d?.max_quota ? Math.round(d.max_quota / 1024 / 1024) : 0,
  }
}

const totalUsers    = computed(() => domains.value.reduce((s, d) => s + d.stats.users, 0))
const totalContacts = computed(() => domains.value.reduce((s, d) => s + d.stats.contacts, 0))
const totalCampaigns = computed(() => domains.value.reduce((s, d) => s + d.stats.campaigns, 0))

const filteredDomains = computed(() => {
  const q = searchQ.value.trim().toLowerCase()
  return q ? domains.value.filter(d => d.name.toLowerCase().includes(q)) : domains.value
})

async function load() {
  loading.value = true
  try {
    // Try new overview endpoint first (requires backend restart after update)
    const res = await axios.get(`${ADMIN_API}/tenants/overview`)
    domains.value = res.data.domains || []
  } catch (e) {
    // Fallback: use existing /domains endpoint with basic info
    try {
      const res = await axios.get(`${ADMIN_API}/domains`, { params: { skip: 0, limit: 200 } })
      const list = Array.isArray(res.data) ? res.data : []
      domains.value = list.map(d => ({
        id: d.id,
        name: d.name,
        active: d.active,
        created_at: d.created_at,
        stats: { users: 0, contacts: 0, campaigns: 0, line_accounts: 0, last_campaign_at: null },
      }))
    } catch (e2) {
      window.$toast?.error('載入失敗', e2.response?.data?.detail || e2.message)
    }
  } finally {
    loading.value = false
  }
}

async function openDetail(d) {
  selectedDomain.value = d
  resetQuotaForm()
  billingSummary.value = null
  detailUsers.value = []
  detailUsersLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/users`, { params: { domain_id: d.id, limit: 50 } })
    detailUsers.value = Array.isArray(res.data) ? res.data : (res.data.items || [])
  } catch { /* ignore */ }
  finally { detailUsersLoading.value = false }
}

async function loadBillingSummary(d) {
  if (!d) return
  billingLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/tenants/${d.id}/billing`)
    billingSummary.value = res.data
  } catch (e) {
    window.$toast?.error('載入帳單失敗', e.response?.data?.detail || e.message)
    billingSummary.value = null
  } finally {
    billingLoading.value = false
  }
}

async function saveQuota() {
  const d = selectedDomain.value
  if (!d) return
  quotaSaving.value = true
  try {
    const payload = {
      max_users: quotaForm.value.max_users || 0,
      max_aliases: quotaForm.value.max_aliases || 0,
      max_quota: (quotaForm.value.max_quota_mb || 0) * 1024 * 1024,
    }
    await axios.put(`${ADMIN_API}/domains/${d.id}`, payload)
    // Reflect changes on the domain object
    d.max_users = payload.max_users
    d.max_aliases = payload.max_aliases
    d.max_quota = payload.max_quota
    window.$toast?.success('配額已儲存', d.name)
  } catch (e) {
    window.$toast?.error('儲存失敗', e.response?.data?.detail || e.message)
  } finally {
    quotaSaving.value = false
  }
}

function openAddDomain() {
  addForm.value = { name: '' }
  showAddModal.value = true
}

async function submitAdd() {
  if (!addForm.value.name) return
  adding.value = true
  try {
    const res = await axios.post(`${ADMIN_API}/domains`, { name: addForm.value.name })
    showAddModal.value = false
    window.$toast?.success('網域已新增', addForm.value.name)
    // Optimistically add to list immediately, then refresh for stats
    const newDomain = res.data
    if (newDomain && newDomain.id) {
      domains.value.push({
        id: newDomain.id,
        name: newDomain.name,
        active: newDomain.active ?? true,
        created_at: newDomain.created_at,
        stats: { users: 0, contacts: 0, campaigns: 0, line_accounts: 0, last_campaign_at: null },
      })
    }
    // Background refresh for accurate stats
    load()
  } catch (e) {
    window.$toast?.error('新增失敗', e.response?.data?.detail || e.message)
  } finally {
    adding.value = false
  }
}

async function confirmDelete(d) {
  if (!confirm(`確定刪除網域「${d.name}」？此操作無法復原，將同時刪除所有使用者與資料。`)) return
  try {
    await axios.delete(`${ADMIN_API}/domains/${d.id}`)
    domains.value = domains.value.filter(x => x.id !== d.id)
    window.$toast?.success('已刪除網域', d.name)
  } catch (e) {
    window.$toast?.error('刪除失敗', e.response?.data?.detail || e.message)
  }
}

function roleLabel(r) {
  return { super_admin: '超級管理員', domain_admin: '網域管理員', user: '一般使用者' }[r] || r || '—'
}
function fmtDate(d) {
  if (!d) return '—'
  try { return new Date(d).toLocaleDateString('zh-TW') } catch { return d }
}

onMounted(load)
</script>

<style scoped>
.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
